import { toaster } from '@/hooks/useToast/toastUtils'
import {
  Clipboard,
  Field,
  HStack,
  Icon,
  IconButton,
  Input,
  Skeleton,
  type StackProps,
} from '@chakra-ui/react'
import lowerCase from 'lodash/lowerCase'
import { CheckIcon, CopyIcon, PenIcon } from 'lucide-react'
import type { MouseEventHandler } from 'react'

type DataInputProps = {
  label: string
  value?: string | number
  isLoading?: boolean
  isEditable?: boolean
  onEditClick?: MouseEventHandler<HTMLButtonElement>
} & StackProps

export function DataInput({
  label,
  value = '',
  isLoading,
  isEditable = false,
  onEditClick = () => void 0,
  ...stackProps
}: DataInputProps) {
  // Clipboard state will be managed by Clipboard.Root component

  const handleCopySuccess = () => {
    toaster.create({
      title: `Copied ${lowerCase(label)} to clipboard`,
      type: 'success',
      duration: 2000,
    })
  }

  return (
    <HStack width="100%" alignItems="end" {...stackProps}>
      <Field.Root readOnly>
        <Field.Label>{label}</Field.Label>
        {isLoading ? (
          <Skeleton w="100%" borderRadius="md" opacity="0.3">
            <Input value={value} />
          </Skeleton>
        ) : (
          <Input value={value} />
        )}
      </Field.Root>

      {isEditable ? (
        <IconButton
          variant="outline"
          aria-label={`Edit ${label}`}
          size="lg"
          onClick={onEditClick}
          css={{
            height: '38px !important',
          }}
        >
          <Icon as={PenIcon} />
        </IconButton>
      ) : null}

      <Clipboard.Root
        value={value as string}
        onStatusChange={(details) => {
          if (details.copied) {
            handleCopySuccess()
          }
        }}
      >
        <Clipboard.Trigger asChild>
          <IconButton
            variant="outline"
            aria-label={`Copy ${label}`}
            size="lg"
            css={{
              height: '38px !important',
            }}
          >
            <Clipboard.Indicator
              copied={<Icon as={CheckIcon} color="success.500" />}
            >
              <Icon as={CopyIcon} />
            </Clipboard.Indicator>
          </IconButton>
        </Clipboard.Trigger>
      </Clipboard.Root>
    </HStack>
  )
}
